"use client"

import { Volume2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { LOUDNESS_PRESETS, FINE_TUNE_GAIN_RANGE, type LoudnessPreset } from "@/lib/audio-utils"

interface LoudnessControlsProps {
  preset: LoudnessPreset
  fineTuneGainDb: number
  measuredLoudnessDb: number | null
  onPresetChange: (preset: LoudnessPreset) => void
  onFineTuneGainChange: (gainDb: number) => void
  disabled?: boolean
  className?: string
}

const PRESET_ORDER: LoudnessPreset[] = ["quiet", "normal", "loud"]

/**
 * Loudness controls for Chime Studio: preset buttons (Quiet / Normal / Loud)
 * plus a fine-tune slider (−30…+6 dB) and a live loudness readout.
 *
 * Copy avoids any claim that presets are "matched to Tesla's stock chime" —
 * they are described only as typical loudness levels (ANTI_SLOP rule 1).
 */
export function LoudnessControls({
  preset,
  fineTuneGainDb,
  measuredLoudnessDb,
  onPresetChange,
  onFineTuneGainChange,
  disabled = false,
  className,
}: LoudnessControlsProps) {
  const fineTunePercent =
    ((fineTuneGainDb - FINE_TUNE_GAIN_RANGE.min) /
      (FINE_TUNE_GAIN_RANGE.max - FINE_TUNE_GAIN_RANGE.min)) *
    100

  const trackStyle = {
    background: `linear-gradient(90deg, var(--tls-brand) 0%, var(--tls-brand) ${fineTunePercent}%, var(--tls-surface-hover) ${fineTunePercent}%, var(--tls-surface-hover) 100%)`,
  }

  const measuredLoudnessLabel =
    measuredLoudnessDb !== null && isFinite(measuredLoudnessDb)
      ? `${measuredLoudnessDb.toFixed(1)} dB`
      : null

  const activePresetLabel = LOUDNESS_PRESETS[preset].label
  const hasFineTuneAdjustment = fineTuneGainDb !== 0

  return (
    <div
      className={cn(
        "space-y-4 rounded-[var(--ls-radius-0)] border border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface-elevated)] p-4",
        className
      )}
    >
      {/* Header row — plain-language summary first, raw dB as secondary detail */}
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-2 text-sm text-[var(--tls-fg-muted)]">
          <Volume2 className="size-4" aria-hidden />
          <span>
            Loudness: {activePresetLabel}
            {hasFineTuneAdjustment ? " (adjusted)" : ""}
          </span>
        </span>
        {measuredLoudnessLabel && (
          <span
            className="text-xs font-sans font-semibold tabular-nums text-[var(--tls-fg-subtle)]"
            aria-live="polite"
            aria-label={`Measured loudness: ${measuredLoudnessLabel}`}
          >
            ({measuredLoudnessLabel})
          </span>
        )}
      </div>

      {/* Preset buttons */}
      <div role="group" aria-label="Loudness preset" className="grid grid-cols-3 gap-1.5">
        {PRESET_ORDER.map((key) => {
          const p = LOUDNESS_PRESETS[key]
          const isActive = preset === key
          return (
            <button
              key={key}
              type="button"
              onClick={() => onPresetChange(key)}
              disabled={disabled}
              aria-pressed={isActive}
              title={p.description}
              aria-describedby={`loudness-preset-desc-${key}`}
              className={cn(
                // min-h ensures 44px WCAG 2.5.5 touch target
                "min-h-[44px] rounded-[var(--ls-radius-0)] border px-2 py-2 text-xs font-medium transition-[border-color,background-color,color] duration-[var(--motion-duration-standard)] ease-[var(--motion-ease-standard)]",
                "tlsr-focus",
                "disabled:cursor-not-allowed disabled:opacity-50",
                isActive
                  ? "border-[color:color-mix(in_srgb,var(--tls-brand)_40%,transparent)] bg-[color:color-mix(in_srgb,var(--tls-brand)_12%,var(--tls-surface))] text-[var(--tls-brand)]"
                  : "border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface)] text-[var(--tls-fg-muted)] hover:border-[color:var(--tls-border-strong)] hover:bg-[var(--tls-surface-hover)] hover:text-[var(--tls-fg-strong)]"
              )}
            >
              {p.label}
              {/* Screen-reader-accessible copy of the tooltip so the description
                  is not locked inside the native title attribute alone. */}
              <span id={`loudness-preset-desc-${key}`} className="sr-only">
                {p.description}
              </span>
            </button>
          )
        })}
      </div>

      {/* Fine-tune slider */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <label
            htmlFor="chime-studio-fine-tune"
            className="text-xs text-[var(--tls-fg-subtle)]"
          >
            Fine-tune
          </label>
          <span className="text-xs font-sans font-semibold tabular-nums text-[var(--tls-fg-muted)]">
            {fineTuneGainDb > 0 ? "+" : ""}{fineTuneGainDb.toFixed(1)} dB
          </span>
        </div>
        <input
          id="chime-studio-fine-tune"
          type="range"
          min={FINE_TUNE_GAIN_RANGE.min}
          max={FINE_TUNE_GAIN_RANGE.max}
          step={0.5}
          value={fineTuneGainDb}
          onChange={(e) => onFineTuneGainChange(Number(e.target.value))}
          disabled={disabled}
          style={trackStyle}
          className={cn(
            "w-full h-2 rounded-[var(--tls-radius-pill)] appearance-none cursor-pointer",
            "[&::-webkit-slider-thumb]:appearance-none",
            "[&::-webkit-slider-thumb]:size-4",
            "[&::-webkit-slider-thumb]:rounded-[var(--tls-radius-pill)] [&::-webkit-slider-thumb]:bg-[var(--tls-fg-strong)]",
            "[&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:border-[color:var(--tls-border-strong)]",
            "[&::-webkit-slider-thumb]:cursor-pointer",
            "[&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:duration-[var(--motion-duration-standard)] [&::-webkit-slider-thumb]:ease-[var(--motion-ease-standard)] [&::-webkit-slider-thumb]:active:scale-110",
            "[&::-moz-range-thumb]:size-4",
            "[&::-moz-range-thumb]:rounded-[var(--tls-radius-pill)] [&::-moz-range-thumb]:bg-[var(--tls-fg-strong)]",
            "[&::-moz-range-thumb]:border [&::-moz-range-thumb]:border-[color:var(--tls-border-strong)] [&::-moz-range-thumb]:cursor-pointer",
            "disabled:opacity-50 disabled:cursor-not-allowed"
          )}
          aria-label="Fine-tune loudness offset in decibels"
          aria-valuemin={FINE_TUNE_GAIN_RANGE.min}
          aria-valuemax={FINE_TUNE_GAIN_RANGE.max}
          aria-valuenow={fineTuneGainDb}
          aria-valuetext={`${fineTuneGainDb > 0 ? "+" : ""}${fineTuneGainDb.toFixed(1)} dB`}
        />
        <div className="flex justify-between">
          <span className="text-xs text-[var(--tls-fg-subtle)]">
            {FINE_TUNE_GAIN_RANGE.min} dB
          </span>
          <span className="text-xs text-[var(--tls-fg-subtle)]">
            +{FINE_TUNE_GAIN_RANGE.max} dB
          </span>
        </div>
      </div>

      <p className="text-xs text-[var(--tls-fg-subtle)]">
        Typical lock-chime loudness. Fine-tune adjusts the level up or down from the preset.
      </p>
    </div>
  )
}
